import { ArrowUpwardOutlined, AutoAwesomeOutlined } from "@mui/icons-material";
import Message from "../MessageV1";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { helperPrompt } from "../../../services/aiChatService";

export default function ChatPanel(props) {
  const {
    styles,
    messages,
    setMessages,
    chatPanel,
    setChatPanel,
    aiMode = false, // Default to false if not provided
    showToast,
    setRemainingXP,
    isSolved,
    currentQuestionIndex,
  } = props;

  const { id: aiChatId } = useParams();
  const historyRef = useRef(null);
  const scrollElemRef = useRef(null);
  const [userPrompt, setUserPrompt] = useState("");
  const [containerScrollDown, setContainerScrollDown] = useState(false);
  const [generatingResponse, setGeneratingResponse] = useState(false);



  /**
   * Handles the submission of a user prompt to get an AI helper response.
   *
   * @async
   * @param {Event} e - The form submission event
   * @returns {Promise<void>}
   * @throws {Error} When there is an error communicating with the AI service
   *
   * @description
   * This function:
   * 1. Prevents the default form submission behavior
   * 2. Validates and trims the user prompt
   * 3. Sets the UI to a "generating" state
   * 4. Sends the prompt to the backend AI service
   * 5. Processes the AI response, including any XP penalties
   * 6. Updates the messages state with both user prompt and AI response
   * 7. Handles errors and displays appropriate toast notifications
   * 8. Resets the UI state regardless of success or failure
   */
  const fetchHelperResponse = async (e) => {
    try {
      //   setOpenToast(false);
      e.preventDefault();
      const _userPrompt = userPrompt.trim();
      if (_userPrompt !== "") {
        setGeneratingResponse(true);
        const data = { userPrompt: _userPrompt, isSolved: isSolved, questionIndex: currentQuestionIndex };
        setMessages((prev) => [
          ...prev,
          { role: "user", content: _userPrompt },
        ]);
        setUserPrompt("");

        const response = await helperPrompt(aiChatId, data);
        if (response?.status !== 200) {
          showToast(
            "Helper AI is busy Or temporarily unavailable. Please try again later.",
            "error"
          );
          return;
        }

        const blocks = response?.data?.data?.block;
        const generatedMessage = blocks.find(
          (block) => block.actor === "system"
        );

        const penaltyAmount = generatedMessage?.content?.xpPenalty;
        if (penaltyAmount) {
          setRemainingXP((prev) => prev - penaltyAmount);
          showToast(`Penalty applied: ${penaltyAmount} XP`, "error");
        }

        setGeneratingResponse(false);
        setMessages((prev) => [
          ...prev,
          { role: "model", content: generatedMessage.content.content },
        ]);
      }
    } catch (error) {
      console.error("Error in (fetchHelperResponse): ", error);
      showToast("Helper AI is busy Or temporarily unavailable. Please try again later", "error");
    } finally {
      setGeneratingResponse(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      e.target.blur();
      fetchHelperResponse(e);
    }
  };

  useEffect(() => {
    if (!historyRef.current) {
      return;
    }

    setTimeout(() => {
      historyRef?.current?.scrollTo({
        top: historyRef.current.scrollHeight,
        behavior: "smooth",
      });
    }, 50);
  }, [containerScrollDown]);

  useEffect(() => {
    if (generatingResponse && historyRef.current) {
      historyRef.current.scrollTo({
        top: historyRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [generatingResponse]);

  return (
    <div className={`${styles.chat_panel} ${chatPanel && styles.open}`}>
      {window.innerWidth <= 800 && (
        <div
          className={styles.collapse_btn}
          onClick={() => setChatPanel(!chatPanel)}
        >
          <AutoAwesomeOutlined className={styles.icon} />
          <span>Need Help?</span>
        </div>
      )}

      <div className={styles.messages_panel} ref={historyRef}>
        {messages?.length ? (
          <div className={styles.messages} ref={scrollElemRef}>
            {messages
              .filter((message) => !message?.hidden)
              .map((message, index) => {
                return (
                  <div
                    key={index}
                    className={`${styles.message} ${
                      message?.role === "model" ? styles.model : styles.user
                    } ${
                      message?.isAttemptResponse &&
                      (message?.isCorrect ? styles.correct : styles.incorrect)
                    }`}
                  >
                    <span>
                      <Message
                        key={index}
                        text={message.content}
                        recommendations={message.recommendations}
                        role={message.role}
                        setContainerScrollDown={setContainerScrollDown}
                      />
                    </span>
                  </div>
                );
              })}
            {generatingResponse && (
              <div className={`${styles.message} ${styles.generating}`}>
                {"MySphere is Thinking..."}
              </div>
            )}
          </div>
        ) : (
          <div className={styles.info_message}>
            {aiMode
              ? `🕹️ MySphere Buddy helps you figure it out, not hand it out. 
                          ⛔ Request the answer, and boom—–5 XP. Earn it the right way!`
              : "Hello! 🤖 I'm MySphere, here to help you with anything you need—whether it's answering questions, solving problems, or guiding you step by step. Just ask away, and let's explore together! 🚀"}
            :
          </div>
        )}
      </div>

      <div className={styles.prompt_container}>
        <div className={styles.wrapper}>
          <textarea
            className={styles.prompt_input}
            type="text"
            placeholder="Enter your message"
            value={userPrompt}
            onChange={(e) => setUserPrompt(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <button
            className={styles.send_btn}
            type="submit"
            disabled={userPrompt.trim() === ""}
            onClick={(e) => fetchHelperResponse(e)}
          >
            <ArrowUpwardOutlined className={styles.icon} />
          </button>
        </div>
      </div>
    </div>
  );
}
